import { type FC, type SVGProps, useMemo, useRef, useState } from "react";
import { Box, Column, Heading, Row, Text } from "@hightouch/htui";
import { format as formatDate } from "date-fns";
import noop from "lodash/noop";
import pluralize from "pluralize";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  ComposedChart,
  Line,
  Legend as RechartsLegend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Text as RechartsText,
  XAxis,
  YAxis,
} from "recharts";

import { Tooltip as HightouchTooltip } from "@hightouch/htui";
import type { CategoricalChartProps } from "recharts/types/chart/generateCategoricalChart";
import { BreakdownTable } from "src/components/analytics/breakdowns/breakdown-table";
import { ChartTooltip } from "src/components/charts/chart-tooltip";
import { Legend } from "src/components/analytics/shared/legend";
import { formatCount } from "src/components/audiences/insights/chart-cards/utils";
import { DEFAULT_CHART_HEIGHT } from "src/components/charts/chart";
import { HeatmapTable } from "src/pages/analytics/heatmap-table";
import { ChartVariant } from "src/pages/analytics/types";
import { CHART_COLORS } from "src/ui/colors";
import { GraphScale } from "./constants";
import {
  AnnotationTooltip,
  type HoveredAnnotation,
  renderAnnotations,
} from "./annotations";
import { Tooltip } from "./tooltip";
import type { ChartAnnotation, GraphSeries } from "./types";
import { valueFormatter } from "./utils";
import {
  GraphType,
  type GroupByColumn,
} from "@hightouch/lib/query/visual/types";
import { getChartLoader } from "src/components/charts/loaders/utils";
import { ChartWithLoaderText } from "src/components/charts/loaders";
import {
  chartBarRadius,
  chartFontStyles,
  chartSeriesStrokeDasharray,
  getColumnRadius,
  getTooltipCursorStyles,
  getXAxisStyles,
  getYAxisStyles,
} from "src/components/charts/chart-utils";
import { renderCartesianGrid } from "src/components/charts/chart-grid";

export type SupportedGraphType =
  | GraphType.Area
  | GraphType.Bar
  | GraphType.Column
  | GraphType.Line
  | GraphType.HeatMap
  | GraphType.BigNumber;

const leftMargin = 8;
/**
 * A note about all the `render[...]` helper functions here:
 *
 * Ideally we would factor these out into proper React components, however
 * Recharts has internal state sharing that breaks custom components: https://github.com/recharts/recharts/issues/2788
 * In Recharts v3 this will work, but until we upgrade we need to use this function pattern.
 * Also see: https://github.com/recharts/recharts/wiki/3.0-migration-guide#custom-components
 */
const renderXAxis = (props?: {
  allowDuplicatedCategory?: boolean;
  categoricalLabels?: string[];
  showEveryCategoricalLabel?: boolean;
  tickAlign?: "left" | "center";
}) => {
  const everyLabel = props?.showEveryCategoricalLabel
    ? props.categoricalLabels
    : undefined;
  return (
    <XAxis
      {...getXAxisStyles({ tickAlign: props?.tickAlign ?? undefined })}
      dataKey="calculatedAt"
      allowDuplicatedCategory={props?.allowDuplicatedCategory ?? false}
      minTickGap={50}
      {...(everyLabel && {
        interval: 0,
        tick: (tickProps: CategoricalTickProps) =>
          renderTruncatedCategoricalTick(tickProps, everyLabel),
      })}
      tickFormatter={(timestamp: number | "auto") => {
        if (timestamp === "auto") return timestamp;
        // For categorical data, use the label from the array
        if (props?.categoricalLabels && typeof timestamp === "number") {
          return props.categoricalLabels[timestamp] ?? String(timestamp);
        }
        // For time-series data, format as date
        return formatDate(timestamp, "LLL d");
      }}
    />
  );
};

const CATEGORICAL_TICK_PADDING_PX = 8;

type CategoricalTickProps = SVGProps<SVGTextElement> & {
  x: number;
  y: number;
  width: number;
  visibleTicksCount: number;
  payload: { value: number };
};

const renderTruncatedCategoricalTick = (
  { width, visibleTicksCount, payload, ...tickProps }: CategoricalTickProps,
  categoricalLabels: string[],
) => {
  const label = categoricalLabels[payload.value] ?? String(payload.value);
  return (
    <g>
      <title>{label}</title>
      <RechartsText
        {...tickProps}
        style={chartFontStyles}
        width={Math.max(
          width / Math.max(visibleTicksCount, 1) - CATEGORICAL_TICK_PADDING_PX,
          0,
        )}
        maxLines={1}
      >
        {label}
      </RechartsText>
    </g>
  );
};

const renderYAxis = ({ scale, max }: { scale: GraphScale; max?: number }) => (
  <YAxis
    {...getYAxisStyles({ allowDecimals: true })}
    {...(max !== undefined && { domain: [0, max], allowDataOverflow: true })}
    tickFormatter={(value) => valueFormatter(value, scale)}
  />
);

const renderLegend = ({
  graphSeries,
  onHoverLine,
}: {
  graphSeries: GraphSeries[];
  onHoverLine: (key: string | undefined) => void;
}) => (
  <RechartsLegend
    align="left"
    verticalAlign="top"
    // Offset the default margin used within the chart so the legend sits flush with the container bounds.
    wrapperStyle={{ marginLeft: `-${leftMargin}px` }}
    content={() => <Legend series={graphSeries} onHover={onHoverLine} />}
  />
);

// Note: prepend an extra string to avoid any potential conflicts with other keys (however unlikely)
const metricKey = (key: string) => `__metricValue__${key}`;
const boundsKey = (key: string) => `__bounds__${key}`;

export type AnalyticsGraphProps = Pick<
  AnalyticsChartProps,
  | "isLoading"
  | "showLoadingText"
  | "categoricalLabels"
  | "showEveryCategoricalLabel"
  | "annotations"
  | "yAxisMax"
> & {
  /** Whether to stack bars in column charts (default: true) */
  stackBars?: boolean;
  graphSeries: GraphSeries[];
  hoveredLine?: string;
  height?: string;
  scale?: GraphScale;
  type?: SupportedGraphType;
  groupByColumns?: GroupByColumn[];
  showConfidenceBounds?: boolean;
  variant?: ChartVariant;
  onHoverLine?: (key: string | undefined) => void;
};

export const AnalyticsGraph: FC<AnalyticsGraphProps> = ({
  isLoading = false,
  showLoadingText = true,
  categoricalLabels,
  showEveryCategoricalLabel,
  annotations,
  yAxisMax,
  stackBars = true,
  height = DEFAULT_CHART_HEIGHT,
  graphSeries,
  hoveredLine,
  scale = GraphScale.Linear,
  type = GraphType.Line,
  groupByColumns,
  // Will only show confidence bounds if `true` and the metric has them, and the chart type supports it
  showConfidenceBounds = false,
  variant = ChartVariant.Full,
  onHoverLine = noop,
}) => {
  // For the stacked area charts, Recharts requires that we group the series data by the X-axis value (calculatedAt)
  // This isn't required for the simple line chart case, but use the same data structure for both anyways
  // eg.
  // {
  //   calculatedAt: Date,
  //   __metricValue__series1: number,
  //   __metricValue__series2: number,
  // }
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const chartData = useMemo(() => {
    const data = graphSeries.reduce<{
      [key: string]: {
        calculatedAt: number;
        [key: string]: number | [number, number];
      };
    }>((acc, series) => {
      series.data.forEach((point) => {
        const prev = acc[point.calculatedAt] ?? {
          calculatedAt: point.calculatedAt,
        };

        prev[metricKey(series.key)] = point.metricValue;

        if (showConfidenceBounds && point.errorBounds) {
          const confidenceBounds: [number, number] = [
            point.errorBounds.lowerBound,
            point.errorBounds.upperBound,
          ];

          prev[boundsKey(series.key)] = confidenceBounds;
        }

        acc[point.calculatedAt] = prev;
      });

      return acc;
    }, {});

    return Object.values(data);
  }, [graphSeries]);

  const chartProps: CategoricalChartProps = {
    data: chartData,
    // Note: try to use padding on parent element instead of changing margin props
    // These values just help the chart look more evenly spaced
    margin: {
      left: leftMargin, // left margin required to show full grid bounds with hover tooltips
      right: -8, // negative right offsets the y-axis labels
    },
    onMouseLeave: () => onHoverLine(undefined),
  };

  return (
    <Column width="100%" height={height}>
      <AnalyticsChart
        isLoading={isLoading}
        showLoadingText={showLoadingText}
        type={type}
        chartProps={chartProps}
        graphSeries={graphSeries}
        hoveredLine={hoveredLine}
        scale={scale}
        groupByColumns={groupByColumns}
        showConfidenceBounds={showConfidenceBounds}
        variant={variant}
        categoricalLabels={categoricalLabels}
        showEveryCategoricalLabel={showEveryCategoricalLabel}
        annotations={annotations}
        yAxisMax={yAxisMax}
        stackBars={stackBars}
        onHoverLine={onHoverLine}
      />
    </Column>
  );
};

type AnalyticsChartProps = {
  /** If `true`, the loader animation will be displayed */
  isLoading?: boolean;
  /** Determines if loading text is shown when loader animation is active */
  showLoadingText?: boolean;
  /** For categorical charts that don't use timestamps, provide the x-axis labels */
  categoricalLabels?: string[];
  /** Column charts only: render every categorical label, truncated to its column's width */
  showEveryCategoricalLabel?: boolean;
  /** Column charts only: dashed markers on the x-axis with hover tooltips. Pass an empty array while markers load to keep the chart mounted. */
  annotations?: ChartAnnotation[];
  /** Column charts only: pins the top of the y-axis, so stacked shares that round past 1 still top out at 100% */
  yAxisMax?: number;
  chartProps: CategoricalChartProps;
  graphSeries: GraphSeries[];
  hoveredLine: string | undefined;
  scale: GraphScale;
  groupByColumns: GroupByColumn[] | undefined;
  showConfidenceBounds: boolean;
  variant: AnalyticsGraphProps["variant"];
  stackBars: boolean;
  onHoverLine: (key: string | undefined) => void;
};

export const AnalyticsChart = ({
  isLoading,
  showLoadingText,
  type,
  ...props
}: AnalyticsChartProps & { type: SupportedGraphType }) => {
  const chartLoader = getChartLoader({ graphType: type });

  if (isLoading) {
    return showLoadingText ? (
      <ChartWithLoaderText boxSize="100%">
        <Column align="center" justify="start" flex={1} w="100%">
          {chartLoader}
        </Column>
      </ChartWithLoaderText>
    ) : (
      chartLoader
    );
  }
  switch (type) {
    case GraphType.BigNumber:
      return <BigNumberChart {...props} />;
    case GraphType.Line:
      return <AnalyticsLineChart {...props} />;
    case GraphType.Area:
      return <AnalyticsAreaChart {...props} />;
    case GraphType.Column:
      return <AnalyticsColumnChart {...props} />;
    case GraphType.Bar:
      return (
        <ResponsiveContainer
          debounce={100}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <BreakdownTable
            enableSearch={false}
            data={props.graphSeries}
            groupByColumns={props.groupByColumns}
            variant={props.variant}
            showHeaders={props.variant == "full"}
          />
        </ResponsiveContainer>
      );
    case GraphType.HeatMap:
      return (
        <HeatmapTable
          isLoading={isLoading}
          data={props.graphSeries}
          scale={props.scale}
          variant={props.variant}
        />
      );
    default:
      return null;
  }
};

const formatBigNumberCount = (count: number) => {
  if (count === 0) return "0";
  if (count < 1) return formatCount(count, 4);

  // Handle whole numbers
  if (Number.isInteger(count)) {
    if (count < 10000) return formatCount(count);
  }

  // Handle large numbers with abbreviations
  const absCount = Math.abs(count);

  if (absCount >= 1_000_000_000_000) {
    // Trillions
    return (count / 1_000_000_000_000).toFixed(2) + "t";
  } else if (absCount >= 1_000_000_000) {
    // Billions
    return (count / 1_000_000_000).toFixed(2) + "b";
  } else if (absCount >= 1_000_000) {
    // Millions
    return (count / 1_000_000).toFixed(2) + "m";
  } else if (absCount >= 1_000) {
    // Thousands
    return (count / 1_000).toFixed(2) + "k";
  } else {
    // Numbers between 1 and 999.99
    return formatCount(count, 2);
  }
};

export const BigNumberCard = ({
  count,
  legend,
  color,
  align = "center",
  size = "lg",
  noun,
  subtitle,
}: {
  count: number;
  legend: string;
  color: string;
  align?: "center" | "left";
  size?: "sm" | "lg";
  noun?: string;
  subtitle?: string;
}) => {
  const headerSize = size === "lg" ? "2xl" : "lg";
  const justify = align === "center" ? "center" : "flex-start";
  const gap = size === "lg" ? 1 : 2;

  return (
    <HightouchTooltip
      placement="bottom"
      message={
        <ChartTooltip
          wrapperVariant="minimal"
          color={color}
          title={legend}
          value={[{ value: formatCount(count) }]}
        />
      }
    >
      <Row
        borderRadius="lg"
        height="100%"
        width="100%"
        alignContent="center"
        p={4}
        textAlign={align}
        overflowX="hidden"
        textStyle="mono"
        transitionProperty="background-color"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        _hover={{
          backgroundColor: "base.background",
        }}
      >
        <Box
          display="flex"
          gap={gap}
          width="100%"
          overflowX="hidden"
          flexDir="column"
          justifyContent="center"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          <Row
            align="center"
            justify={justify}
            gap={2}
            maxW="240px"
            mx={justify === "center" ? "auto" : undefined}
          >
            {/* TODO @Taylor : Rotate through shapes here. */}
            <Box
              background={color}
              boxSize={2}
              borderRadius="xs"
              flexShrink={0}
            />
            <Text
              isTruncated
              size="sm"
              color="text.secondary"
              variant="monospace"
            >
              {legend}
            </Text>
          </Row>
          <Heading size={headerSize} variant="monospace">
            {formatBigNumberCount(count)}
          </Heading>
          {noun && (
            <Text size="sm" color="text.secondary" variant="monospace">
              {pluralize(noun, count)}
            </Text>
          )}
          {subtitle && (
            <Text size="sm" color="text.tertiary">
              {subtitle}
            </Text>
          )}
        </Box>
      </Row>
    </HightouchTooltip>
  );
};

const BigNumberChart = ({ graphSeries }: AnalyticsChartProps) => {
  const [width, setWidth] = useState(0);

  const maxGroupSize = useMemo(() => {
    if (graphSeries.length <= 3) return graphSeries.length;
    if (graphSeries.length === 4) return 2;
    if (graphSeries.length <= 6) return 3;

    return 4;
  }, [graphSeries.length]);

  const BIG_NUMBER_BREAKPOINT = 168 * maxGroupSize; // 168px is min-width for lg size to allow 7 chars for the value + padding

  const align = graphSeries.length >= 7 ? "left" : "center";
  const size =
    width < BIG_NUMBER_BREAKPOINT || graphSeries.length >= 9 ? "sm" : "lg";

  const groupedGraphSeries = useMemo(() => {
    if (graphSeries.length <= 6) {
      // For smaller groups, use the simple approach
      return graphSeries.reduce((acc, series, index) => {
        const groupIndex = Math.floor(index / maxGroupSize);
        acc[groupIndex] = [...(acc[groupIndex] || []), series];
        return acc;
      }, [] as GraphSeries[][]);
    }

    // For larger groups, distribute more evenly
    const totalGroups = Math.ceil(graphSeries.length / maxGroupSize);
    const baseSize = Math.floor(graphSeries.length / totalGroups);
    const remainder = graphSeries.length % totalGroups;

    const groups: GraphSeries[][] = [];
    let currentIndex = 0;

    for (let i = 0; i < totalGroups; i++) {
      const groupSize = baseSize + (i < remainder ? 1 : 0);
      groups.push(graphSeries.slice(currentIndex, currentIndex + groupSize));
      currentIndex += groupSize;
    }

    return groups;
  }, [graphSeries, maxGroupSize]);

  return (
    <ResponsiveContainer onResize={(width, _height) => setWidth(width)}>
      <Column align="center" justify="center" overflow="hidden" height="100%">
        {groupedGraphSeries.map((group, groupIndex) => {
          return (
            <Box
              key={groupIndex}
              width="100%"
              height="100%"
              display="grid"
              gridTemplateColumns={`repeat(${group.length}, 1fr)`}
              alignItems="center"
              justifyContent="center"
            >
              {group.map((series) => {
                return (
                  <BigNumberCard
                    key={series.key}
                    // Most cases that use this chart will be given a series length of 1
                    // because they're using the "all" frequency.
                    // In cases where an actual timeseries is given (length > 1), we take the most recent
                    // datapoint to display.
                    count={series.data.at(-1)?.metricValue ?? 0}
                    legend={series.legendName}
                    color={series.color}
                    align={align}
                    size={size}
                    noun={series.metricNoun}
                  />
                );
              })}
            </Box>
          );
        })}
      </Column>
    </ResponsiveContainer>
  );
};

// Note: until we upgrade to Recharts v3, each component needs a <ResponsiveContainer> wrapper
// See: https://github.com/recharts/recharts/issues/2788#issuecomment-2675576335
const AnalyticsLineChart = ({
  chartProps,
  graphSeries,
  hoveredLine,
  scale,
  showConfidenceBounds,
  categoricalLabels,
  onHoverLine,
}: AnalyticsChartProps) => (
  <ResponsiveContainer>
    <ComposedChart {...chartProps}>
      {renderCartesianGrid({ vertical: true })}
      {renderXAxis({ categoricalLabels })}
      {renderYAxis({ scale })}
      <RechartsTooltip
        cursor={getTooltipCursorStyles()}
        content={
          <Tooltip
            graphSeries={graphSeries}
            hoveredLine={hoveredLine}
            scale={scale}
            categoricalLabels={categoricalLabels}
          />
        }
      />
      <>
        {/* Note: render confidence bounds first so that they are below all the lines */}
        {showConfidenceBounds &&
          graphSeries.map(({ key, description, color }) => (
            <Area
              key={boundsKey(key)}
              dataKey={boundsKey(key)}
              isAnimationActive={false}
              name={description}
              dot={false}
              activeDot={false}
              stroke="none"
              fill={color}
              connectNulls
              onMouseEnter={() => onHoverLine(key)}
              opacity={hoveredLine ? (hoveredLine === key ? 0.3 : 0.1) : 0.3}
            />
          ))}
        {graphSeries.map(({ key, description, color, style = "solid" }) => (
          <Line
            key={metricKey(key)}
            dataKey={metricKey(key)}
            isAnimationActive={false}
            name={description}
            dot={false}
            activeDot={{
              r: 6,
              onMouseEnter: () => onHoverLine(key),
            }}
            strokeWidth={2}
            stroke={color}
            strokeDasharray={
              style === "dashed" ? chartSeriesStrokeDasharray : undefined
            }
            onMouseEnter={() => onHoverLine(key)}
            opacity={hoveredLine ? (hoveredLine === key ? 1 : 0.2) : 1}
          />
        ))}
      </>
      {renderLegend({ graphSeries, onHoverLine })}
    </ComposedChart>
  </ResponsiveContainer>
);

const AnalyticsAreaChart = ({
  chartProps,
  graphSeries,
  hoveredLine,
  onHoverLine,
  scale,
  categoricalLabels,
}: AnalyticsChartProps) => (
  <ResponsiveContainer>
    <AreaChart {...chartProps}>
      {renderCartesianGrid({ vertical: true })}
      {renderXAxis({ categoricalLabels })}
      {renderYAxis({ scale })}
      <RechartsTooltip
        cursor={getTooltipCursorStyles()}
        content={
          <Tooltip
            graphSeries={graphSeries}
            hoveredLine={hoveredLine}
            scale={scale}
            categoricalLabels={categoricalLabels}
          />
        }
      />
      {graphSeries.map(({ key, description, color, lightColor }) => (
        <Area
          key={key}
          dataKey={metricKey(key)}
          isAnimationActive={false}
          name={description}
          stackId="stack1"
          dot={false}
          activeDot={{
            r: 6,
            onMouseEnter: () => onHoverLine(key),
          }}
          stroke={color}
          fill={lightColor ?? color}
          onMouseEnter={() => onHoverLine(key)}
          opacity={hoveredLine ? (hoveredLine === key ? 1 : 0.2) : 1}
        />
      ))}
      {renderLegend({ graphSeries, onHoverLine })}
    </AreaChart>
  </ResponsiveContainer>
);

const AnalyticsColumnChart = ({
  chartProps,
  graphSeries,
  hoveredLine,
  scale,
  categoricalLabels,
  showEveryCategoricalLabel,
  annotations,
  yAxisMax,
  stackBars,
  onHoverLine,
}: AnalyticsChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredAnnotation, setHoveredAnnotation] =
    useState<HoveredAnnotation>();
  const hasAnnotations = annotations !== undefined;

  const chart = (
    <ResponsiveContainer>
      <BarChart {...chartProps}>
        {renderCartesianGrid()}
        {/* Duplicated category is required for stack bar chart tooltips to work */}
        {renderXAxis({
          allowDuplicatedCategory: true,
          tickAlign: "center",
          categoricalLabels,
          showEveryCategoricalLabel,
        })}
        {renderYAxis({ scale, max: yAxisMax })}
        <RechartsTooltip
          // The bar tooltip would cover the marker's own tooltip.
          {...(hoveredAnnotation && { active: false })}
          cursor={{ fill: CHART_COLORS.gray.divider, fillOpacity: 0.6 }}
          content={
            <Tooltip
              graphSeries={graphSeries}
              hoveredLine={hoveredLine}
              scale={scale}
              categoricalLabels={categoricalLabels}
            />
          }
        />
        {graphSeries.map(({ key, description, color, data }) => (
          <Bar
            key={key}
            dataKey={metricKey(key)}
            isAnimationActive={false}
            name={description}
            stackId={stackBars ? "stack1" : undefined}
            strokeWidth={1}
            stroke="white"
            paintOrder="stroke"
            fill={color}
            opacity={hoveredLine ? (hoveredLine === key ? 1 : 0.2) : 1}
            radius={
              // If there are stacked bars, use a consistent radius for all corners,
              //  otherwise only round the top two corners.
              graphSeries.length > 1
                ? chartBarRadius.md
                : getColumnRadius(chartProps?.data?.length ?? 0)
            }
            style={{
              transform: "translateY(-1px)",
            }}
            onMouseEnter={() => onHoverLine(key)}
          >
            {/* A data point may override the series color for its own bar
                (e.g. agent-pinned bar colors). Only render Cells when a
                point sets one, so other column charts keep the plain fill. */}
            {data.some((point) => point.color) &&
              chartProps.data?.map((row) => (
                <Cell
                  key={row.calculatedAt}
                  fill={
                    data.find(
                      (point) => point.calculatedAt === row.calculatedAt,
                    )?.color ?? color
                  }
                />
              ))}
          </Bar>
        ))}
        {annotations &&
          renderAnnotations({ annotations, onHover: setHoveredAnnotation })}
        {renderLegend({ graphSeries, onHoverLine })}
      </BarChart>
    </ResponsiveContainer>
  );

  if (!hasAnnotations) return chart;

  // The marker tooltip positions itself against this box.
  return (
    <Box ref={containerRef} position="relative" width="100%" height="100%">
      {chart}
      {hoveredAnnotation && (
        <AnnotationTooltip
          hovered={hoveredAnnotation}
          containerWidth={containerRef.current?.clientWidth ?? 0}
        />
      )}
    </Box>
  );
};
